/**
 * OnboardingTour — orquestrador principal do tour cinemático Dr. Pera IA.
 *
 * Fluxo:
 * - Step 0 'welcome'  → BootSequence fullscreen
 * - Steps 1-7         → Spotlight + StepTooltip
 * - Step 1 'profile'  → Pausa + abre ProfileModal real (via callback onOpenProfile)
 * - Step 8 'final'    → FinalScreen fullscreen
 *
 * Integração: App.tsx passa onOpenProfile e onProfileClose pra sincronizar com
 * o ProfileModal existente. O tour fica oculto enquanto o modal está aberto.
 *
 * Patch mínimo necessário em App.tsx: ver App.tsx.patch nesta pasta.
 */
import React, { useEffect } from 'react';
import { BootSequence } from './BootSequence';
import { Spotlight } from './Spotlight';
import { StepTooltip } from './StepTooltip';
import { useOnboardingState } from '../hooks/useOnboardingState';
import { ONBOARDING_STEPS } from '../utils/onboardingSteps';
import '../styles/onboarding.css';

interface OnboardingTourProps {
  /** Função de tradução i18n da app */
  t: (key: string) => string;
  /** Locale atual */
  locale: string;
  /**
   * Callback para abrir o ProfileModal real da app.
   * Chamado quando step 'profile' é ativado.
   */
  onOpenProfile: () => void;
  /**
   * Callback para FORÇAR o fechamento do ProfileModal (proteção anti-bug
   * quando o tour avança e o modal não fecha sozinho).
   */
  onCloseProfile?: () => void;
  /**
   * Sinaliza ao OnboardingTour que o ProfileModal foi fechado.
   * App.tsx deve chamar isso via onClose do ProfileModal quando tour está ativo.
   */
  profileModalClosed: boolean;
  /** ID do usuário logado — chave de storage do tour é por médico */
  userId?: string | null;
}

/** Tela final do onboarding — fullscreen */
function FinalScreen({
  onComplete,
  t,
}: {
  onComplete: () => void;
  t: (key: string) => string;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onComplete();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onComplete]);

  return (
    <div
      className="onb-final"
      role="dialog"
      aria-modal="true"
      aria-label={t('onboarding.final.title')}
    >
      {/* Pera com glow forte — Fix 24/05/2026 Dr. Lu: redondo coral */}
      <img
        src="/icons/icon-128.png"
        alt="Dr. Pera"
        width={80}
        height={80}
        style={{
          borderRadius: '50%',
          objectFit: 'cover',
          filter: 'drop-shadow(0 0 18px rgba(124,124,248,0.85)) drop-shadow(0 0 36px rgba(124,124,248,0.45))',
        }}
      />

      <div className="onb-final__title">
        {t('onboarding.final.title')}
      </div>

      <div className="onb-final__desc">
        {t('onboarding.final.subtitle')}
      </div>

      <button
        type="button"
        className="onb-cta"
        onClick={onComplete}
        autoFocus
        style={{
          /* Sem animação de entrada — já aparece pronto */
          opacity: 1,
          animation: 'onb-cta-pulse 2s ease-in-out infinite',
        }}
      >
        [ {t('onboarding.final.cta')} ]
      </button>
    </div>
  );
}

export function OnboardingTour({
  t,
  locale: _locale,
  onOpenProfile,
  onCloseProfile,
  profileModalClosed,
  userId,
}: OnboardingTourProps) {
  const onboarding = useOnboardingState(userId);

  // Quando ProfileModal fecha e estávamos pausados → resume o tour
  useEffect(() => {
    if (profileModalClosed && onboarding.pauseForModal) {
      onboarding.resumeFromPause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileModalClosed]);

  // Abre o ProfileModal real quando pauseForModal ativa
  useEffect(() => {
    if (onboarding.pauseForModal) {
      onOpenProfile();
    }
  }, [onboarding.pauseForModal, onOpenProfile]);

  // Garantia anti-bug DURANTE o tour: quando o step ATIVO não é o de perfil,
  // força fechar o modal de perfil. Cobre o caso de o tour avançar sem que
  // resumeFromPause tenha disparado onClose corretamente.
  //
  // FIX 09/05/2026 (Dr. Lu): adicionado guard `onboarding.shouldShow` —
  // sem ele, este useEffect rodava DEPOIS do tour terminar e fechava o
  // ProfileModal toda vez que médico abria manualmente clicando no Header.
  // Sintoma reportado: "perfil aparece milissegundos e desaparece".
  useEffect(() => {
    if (!onboarding.shouldShow) return; // tour terminado/skipado → não interfere
    const step = ONBOARDING_STEPS[onboarding.currentStep];
    if (step && step.id !== 'profile' && onCloseProfile && !onboarding.pauseForModal) {
      onCloseProfile();
    }
  }, [onboarding.shouldShow, onboarding.currentStep, onboarding.pauseForModal, onCloseProfile]);

  if (!onboarding.shouldShow) return null;

  const currentStep = ONBOARDING_STEPS[onboarding.currentStep];
  if (!currentStep) return null;

  // Step 0 — Welcome
  if (currentStep.id === 'welcome') {
    return <BootSequence onComplete={onboarding.goNext} t={t} />;
  }

  // Step final
  if (currentStep.id === 'final') {
    return <FinalScreen onComplete={onboarding.complete} t={t} />;
  }

  // Oculto enquanto ProfileModal está aberto (pauseForModal = true)
  if (onboarding.pauseForModal) {
    return null;
  }

  // Steps intermediários — Spotlight + Tooltip
  return (
    <>
      <Spotlight target={currentStep.selectorTarget} />
      <StepTooltip
        step={currentStep}
        index={onboarding.currentStep}
        total={onboarding.totalSteps}
        onNext={onboarding.goNext}
        onBack={onboarding.goBack}
        onSkip={onboarding.skip}
        t={t}
      />
    </>
  );
}
